/*---------------------------------------------------------------------------------------------
 * Copyright (c) Bentley Systems, Incorporated. All rights reserved.
 * See LICENSE.md in the project root for license terms and full copyright notice.
 *--------------------------------------------------------------------------------------------*/
import { Id64, Id64Set, Id64String } from "@itwin/core-bentley";
import {
  DrawingViewState,
  IModelConnection,
  SpatialViewState,
  ViewState,
} from "@itwin/core-frontend";

export class ViewSetup {
  /** Queries for and loads the default view for an iModel. */
  public static getDefaultView = async (
    imodel: IModelConnection
  ): Promise<ViewState> => {
    const viewId = await ViewSetup.getFirstViewDefinitionId(imodel);

        // Load the view state using the viewSpec's ID
         
        const viewState = await imodel.views.load(viewId);
        return viewState;
    };

    /** Pick the first available spatial view definition in the imodel */
    private static async getFirstViewDefinitionId(imodel: IModelConnection): Promise<Id64String> {
        // Return default view definition (if any)
        const defaultViewId = await imodel.views.queryDefaultViewId();
        if (Id64.isValid(defaultViewId))
            return defaultViewId;
        // Return first spatial view definition (if any)
        const spatialViews: IModelConnection.ViewSpec[] = await imodel.views.getViewList({ from: SpatialViewState.classFullName });
        if (spatialViews.length > 0)
            return spatialViews[0].id;

    // Return first drawing view definition (if any)
    const drawingViews: IModelConnection.ViewSpec[] =
      await imodel.views.getViewList({ from: DrawingViewState.classFullName });
    if (drawingViews.length > 0) return drawingViews[0].id;

    throw new Error("No valid view definitions in imodel");
  }

  /** Returns the aspect ration of the container the view will be created in. */
  public static getAspectRatio(): number | undefined {
    const viewDiv = document.getElementById("sample-container");

    if (null === viewDiv) return undefined;

    return viewDiv.clientWidth / viewDiv.clientHeight;
  }

  /** Returns a set of every model's id in the iModel. */
  public static async getModelIds(
    iModel: IModelConnection,
    ...modelNames: string[]
  ): Promise<Id64Set> {
    const ids = new Set<string>();
    if (!iModel.isClosed) {
      const query = `SELECT ECInstanceId FROM Bis:PhysicalPartition${
        modelNames.length > 0
          ? ` WHERE codeValue IN ('${modelNames.join("','")}')`
          : ""
      }`;
      for await (const row of iModel.query(query)) {
        ids.add(row[0]);
      }
    }
    return ids;
  }
}
